import React from 'react';
import { Form } from 'react-bootstrap';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

interface Props {
    /** When true, render a read-only text input showing the option's label so the user can select/copy the text. Otherwise render a normal <Form.Select>. */
    readOnly: boolean;
    name: string;
    options?: SelectValueLabel[];
    /** The currently selected value (controlled or default). */
    value?: string;
    placeholder?: string;
    required?: boolean;
    onChange?: (e: any) => void;
    /** Force key remount of the underlying select (used by some screens to refresh defaults). */
    selectKey?: string | number;
}

/**
 * Drop-in replacement for the disabled `<Form.Select>` pattern used in details views.
 * - In edit mode: behaves like a normal `<Form.Select>` populated with `options`.
 * - In read-only mode: renders a `<Form.Control readOnly>` showing the *label* of the
 *   selected option (instead of a disabled `<select>` whose text the browser refuses
 *   to let the user copy).
 */
const ReadOnlySelect: React.FC<Props> = ({ readOnly, name, options, value, placeholder, required, onChange, selectKey }) => {
    if (readOnly) {
        const selectedLabel = options?.find(o => o.value === value)?.label ?? '';
        return (
            <Form.Control
                type="text"
                name={name}
                value={selectedLabel}
                readOnly
                placeholder={placeholder}
            />
        );
    }
    return (
        <Form.Select
            key={selectKey}
            required={required}
            name={name}
            defaultValue={value}
            onChange={onChange}
        >
            <option value={''}>{placeholder}</option>
            {options && options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </Form.Select>
    );
};

export default ReadOnlySelect;
